import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, UserPlus, Copy, Trash2, CheckCircle2, Clock,
  Link as LinkIcon, Search, Shield, User, Building2, Mail,
  CalendarDays, Factory, MapPin, Pencil, Phone
} from 'lucide-react';

const INVITE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'internal', label: 'Internal' },
  { value: 'super_admin', label: 'Super Admin' },
];

const roleConfig = {
  super_admin: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100', label: 'Super Admin', icon: Shield },
  admin: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100', label: 'Admin', icon: Shield },
  merchant: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100', label: 'Merchant', icon: User },
  buyer: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100', label: 'Buyer', icon: Building2 },
  internal: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-100', label: 'Internal', icon: User },
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [activeTab, setActiveTab] = useState('users');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'merchant', company: '', factory_id: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ name: '', role: '', company: '', factory_id: '' });
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'merchant', name: '', company: '', factory_id: '' });
  const [factories, setFactories] = useState([]);
  const [showAddFactory, setShowAddFactory] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [factoryForm, setFactoryForm] = useState({ name: '', location: '' });
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [buyerForm, setBuyerForm] = useState({ name: '', email: '', contact: '', company: '' });

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, invitesRes, factoriesRes] = await Promise.all([
        api.get('/users'),
        api.get('/invites'),
        api.get('/factories'),
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
      setFactories(factoriesRes.data);
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateUser = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('Name, email & password required'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.factory_id === '') delete payload.factory_id;
      await api.post('/users', payload);
      toast.success('User created successfully');
      setShowAddUser(false);
      setForm({ name: '', email: '', password: '', role: 'merchant', company: '', factory_id: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating user'); }
    setSaving(false);
  };

  const handleInvite = async () => {
    if (!inviteForm.email) { toast.error('Email is required'); return; }
    setSaving(true);
    try {
      const payload = { ...inviteForm };
      if (payload.factory_id === '') delete payload.factory_id;
      await api.post('/invites', payload);
      toast.success('Invite created — copy the link to share');
      setShowInvite(false);
      setInviteForm({ email: '', role: 'merchant', name: '', company: '', factory_id: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await api.delete(`/invites/${inviteId}`);
      toast.success('Invite removed');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(token);
    toast.success('Invite link copied to clipboard');
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleSaveFactory = async () => {
    if (!factoryForm.name || !factoryForm.location) { toast.error('Name and Location required'); return; }
    setSaving(true);
    try {
      if (editingFactory) {
        await api.put(`/factories/${editingFactory.id}`, factoryForm);
        toast.success('Factory updated');
      } else {
        await api.post('/factories', factoryForm);
        toast.success('Factory created');
      }
      setShowAddFactory(false);
      setEditingFactory(null);
      setFactoryForm({ name: '', location: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving factory'); }
    setSaving(false);
  };

  const handleDeleteFactory = async (factoryId) => {
    if (!window.confirm('Delete this factory?')) return;
    try {
      await api.delete(`/factories/${factoryId}`);
      toast.success('Factory deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete factory'); }
  };

  const openEditFactory = (factory) => {
    setEditingFactory(factory);
    setFactoryForm({ name: factory.name, location: factory.location });
    setShowAddFactory(true);
  };

  // ===== BUYER CRUD =====
  const buyers = users.filter(u => u.role === 'buyer');

  const handleSaveBuyer = async () => {
    if (!buyerForm.name || !buyerForm.company) { toast.error('Buyer Name and Company required'); return; }
    if (!editingBuyer && !buyerForm.email) { toast.error('Buyer Email required'); return; }
    setSaving(true);
    try {
      if (editingBuyer) {
        await api.put(`/users/${editingBuyer.id}`, {
          name: buyerForm.name,
          contact: buyerForm.contact,
          company: buyerForm.company,
        });
        toast.success('Buyer updated');
      } else {
        await api.post('/users', {
          name: buyerForm.name,
          email: buyerForm.email.trim().toLowerCase(),
          password: `buyer${Date.now()}`,
          role: 'buyer',
          company: buyerForm.company,
          contact: buyerForm.contact,
        });
        toast.success('Buyer added');
      }
      setShowAddBuyer(false);
      setEditingBuyer(null);
      setBuyerForm({ name: '', email: '', contact: '', company: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving buyer'); }
    setSaving(false);
  };

  const openEditBuyer = (buyer) => {
    setEditingBuyer(buyer);
    setBuyerForm({ name: buyer.name, email: buyer.email || '', contact: buyer.contact || '', company: buyer.company || '' });
    setShowAddBuyer(true);
  };

  const handleDeleteBuyer = async (buyerId) => {
    if (!window.confirm('Delete this buyer?')) return;
    try {
      await api.delete(`/users/${buyerId}`);
      toast.success('Buyer deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete buyer'); }
  };

  // ===== EDIT USER =====
  const [userPerms, setUserPerms] = useState({ can_view_orders: true, can_edit_orders: true, can_view_floor: true, can_edit_floor: true });

  const openEditUser = async (u) => {
    setEditingUser(u);
    setEditUserForm({ name: u.name, role: u.role, company: u.company || '', factory_id: u.factory_id || '' });
    setShowEditUser(true);
    try {
      const res = await api.get(`/users/${u.id}/permissions`);
      setUserPerms(res.data);
    } catch { setUserPerms({ can_view_orders: true, can_edit_orders: true, can_view_floor: true, can_edit_floor: true }); }
  };

  const handleUpdateUser = async () => {
    if (!editUserForm.name || !editUserForm.role) { toast.error('Name and Role required'); return; }
    setSaving(true);
    try {
      const payload = { ...editUserForm };
      if (payload.factory_id === '') payload.factory_id = null;
      await api.put(`/users/${editingUser.id}`, payload);
      if (isSuperAdmin) {
        await api.put(`/users/${editingUser.id}/permissions`, userPerms);
      }
      toast.success('User updated');
      setShowEditUser(false);
      setEditingUser(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating user'); }
    setSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  // Filter users
  const factoryMap = {};
  factories.forEach(f => { factoryMap[f.id] = f.name; });

  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchQuery ||
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const pendingInvites = invites.filter(i => !i.accepted);
  const acceptedInvites = invites.filter(i => i.accepted);

  // Role counts
  const roleCounts = {};
  users.forEach(u => { roleCounts[u.role] = (roleCounts[u.role] || 0) + 1; });

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div data-testid="admin-users-page" className="max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-[#0A0A0A]">User Management</h1>
            <p className="text-sm text-[#71717A] mt-1">{users.length} users &middot; {pendingInvites.length} pending invites</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showInvite} onOpenChange={setShowInvite}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-md text-sm border-black/[0.12]" data-testid="invite-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Invite User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="font-heading text-lg">Invite User</DialogTitle></DialogHeader>
                <p className="text-xs text-[#71717A] -mt-1">Create a shareable invite link. The user will set their own password.</p>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Email *</Label>
                    <Input value={inviteForm.email} onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))} placeholder="user@company.com" className="h-10 rounded-md" data-testid="invite-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Role *</Label>
                    <Select value={inviteForm.role} onValueChange={(v) => setInviteForm(p => ({ ...p, role: v }))}>
                      <SelectTrigger className="h-10 rounded-md" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INVITE_ROLES.filter(r => isSuperAdmin || r.value !== 'super_admin').map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Name</Label>
                      <Input value={inviteForm.name} onChange={(e) => setInviteForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="h-10 rounded-md" data-testid="invite-name-field" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Company</Label>
                      <Input value={inviteForm.company} onChange={(e) => setInviteForm(p => ({ ...p, company: e.target.value }))} placeholder="Company name" className="h-10 rounded-md" data-testid="invite-company-field" />
                    </div>
                  </div>
                  {inviteForm.role === 'internal' && factories.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Assign Factory</Label>
                      <Select value={inviteForm.factory_id || 'none'} onValueChange={(v) => setInviteForm(p => ({ ...p, factory_id: v === 'none' ? '' : v }))}>
                        <SelectTrigger className="h-10 rounded-md" data-testid="invite-factory-select"><SelectValue placeholder="Select factory" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Factory</SelectItem>
                          {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name} — {f.location}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-[#A1A1AA]">Internal user will see all orders from this factory</p>
                    </div>
                  )}
                  <Button onClick={handleInvite} disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="send-invite-btn">
                    {saving ? 'Creating...' : 'Create Invite Link'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
              <DialogTrigger asChild>
                <Button className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm btn-premium" data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="font-heading text-lg">Create User</DialogTitle></DialogHeader>
                <p className="text-xs text-[#71717A] -mt-1">Directly create a user account with credentials.</p>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Full Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Rahul Sharma" className="h-10 rounded-md" data-testid="user-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Email *</Label>
                    <Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="user@company.com" className="h-10 rounded-md" data-testid="user-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Password *</Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" className="h-10 rounded-md" data-testid="user-password-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Role *</Label>
                      <Select value={form.role} onValueChange={(v) => setForm(p => ({ ...p, role: v }))}>
                        <SelectTrigger className="h-10 rounded-md" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="merchant">Merchant</SelectItem>
                          <SelectItem value="buyer">Buyer</SelectItem>
                          <SelectItem value="internal">Internal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Company</Label>
                      <Input value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Company name" className="h-10 rounded-md" data-testid="user-company-input" />
                    </div>
                  </div>
                  {form.role === 'internal' && factories.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Assign Factory</Label>
                      <Select value={form.factory_id || 'none'} onValueChange={(v) => setForm(p => ({ ...p, factory_id: v === 'none' ? '' : v }))}>
                        <SelectTrigger className="h-10 rounded-md" data-testid="user-factory-select"><SelectValue placeholder="Select factory" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Factory</SelectItem>
                          {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name} — {f.location}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-[#A1A1AA]">Internal user will see all orders from this factory</p>
                    </div>
                  )}
                  <Button onClick={handleCreateUser} disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="save-user-btn">
                    {saving ? 'Creating...' : 'Create User'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Role Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(roleConfig).map(([role, config]) => {
            const count = roleCounts[role] || 0;
            const RIcon = config.icon;
            return (
              <button
                key={role}
                onClick={() => setFilterRole(filterRole === role ? 'all' : role)}
                className={`p-3 rounded-md border text-left transition-all ${filterRole === role ? `${config.bg} ${config.border} ring-1 ring-offset-1` : 'bg-white border-black/[0.06] hover:border-black/[0.12]'}`}
                data-testid={`filter-role-${role}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <RIcon className={`w-3.5 h-3.5 ${config.text}`} />
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.text}`}>{config.label}</span>
                </div>
                <p className="text-xl font-heading font-semibold text-[#0A0A0A]">{count}</p>
              </button>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-black/[0.06]">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'users' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
            data-testid="tab-users"
          >
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('buyers')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'buyers' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
            data-testid="tab-buyers"
          >
            Buyers ({buyers.length})
          </button>
          <button
            onClick={() => setActiveTab('factories')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'factories' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
            data-testid="tab-factories"
          >
            Factories ({factories.length})
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'invites' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
            data-testid="tab-invites"
          >
            Invites {pendingInvites.length > 0 && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{pendingInvites.length}</span>}
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* Search */}
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or company..."
                className="h-9 pl-10 rounded-md text-sm"
                data-testid="user-search-input"
              />
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden" data-testid="users-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FAFAF8] border-b border-black/[0.06]">
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">User</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Email</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Role</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Company</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Joined</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#A1A1AA] py-8">{searchQuery || filterRole !== 'all' ? 'No users match the filters' : 'No users yet. Create one or send an invite.'}</TableCell></TableRow>
                  ) : filteredUsers.map(u => {
                    const rc = roleConfig[u.role] || roleConfig.internal;
                    return (
                      <TableRow key={u.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full ${rc.bg} flex items-center justify-center flex-shrink-0`}>
                              <span className={`text-xs font-semibold ${rc.text}`}>{(u.name || 'U')[0].toUpperCase()}</span>
                            </div>
                            <span className="text-sm font-medium text-[#0A0A0A]">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3 h-3 text-[#A1A1AA]" />
                            <span className="text-sm text-[#52525B]">{u.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] font-semibold px-2 py-1 rounded ${rc.bg} ${rc.text} ${rc.border} border`}>
                            {rc.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          {u.company ? (
                            <div className="flex items-center gap-1.5">
                              <Building2 className="w-3 h-3 text-[#A1A1AA]" />
                              <span className="text-sm text-[#52525B]">{u.company}</span>
                            </div>
                          ) : <span className="text-sm text-[#D4D4D8]">-</span>}
                          {u.role === 'internal' && u.factory_id && factoryMap[u.factory_id] && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Factory className="w-3 h-3 text-cyan-500" />
                              <span className="text-[11px] text-cyan-700 font-medium">{factoryMap[u.factory_id]}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <CalendarDays className="w-3 h-3 text-[#A1A1AA]" />
                            <span className="text-xs text-[#71717A]">{formatDate(u.created_at)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {isSuperAdmin && (
                            <button onClick={() => openEditUser(u)} className="p-1.5 rounded-md hover:bg-blue-50 text-[#A1A1AA] hover:text-blue-600 transition-colors" data-testid={`edit-user-${u.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Buyers Tab */}
        {activeTab === 'buyers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA]">{buyers.length} Buyers</p>
              <Button
                onClick={() => { setEditingBuyer(null); setBuyerForm({ name: '', contact: '', company: '' }); setShowAddBuyer(true); }}
                className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm btn-premium"
                data-testid="add-buyer-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Buyer
              </Button>
            </div>
            {buyers.length === 0 ? (
              <div className="bg-white rounded-md border border-black/[0.06] p-10 text-center">
                <Building2 className="w-10 h-10 text-[#D4D4D8] mx-auto mb-3" />
                <p className="text-sm text-[#A1A1AA]">No buyers added yet</p>
                <p className="text-xs text-[#D4D4D8] mt-1">Add your first buyer to start creating orders</p>
              </div>
            ) : (
              <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden" data-testid="buyers-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAF8] border-b border-black/[0.06]">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Buyer Name</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Email</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Buyer Contact</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Buyer Company</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Added</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyers.map(b => (
                      <TableRow key={b.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`buyer-row-${b.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                              <User className="w-3.5 h-3.5 text-emerald-700" />
                            </div>
                            <span className="text-sm font-medium text-[#0A0A0A]">{b.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3 h-3 text-[#A1A1AA]" />
                            <span className="text-sm text-[#52525B]">{b.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3 h-3 text-[#A1A1AA]" />
                            <span className="text-sm text-[#52525B]">{b.contact || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3 h-3 text-[#A1A1AA]" />
                            <span className="text-sm text-[#52525B]">{b.company || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-[#71717A]">{formatDate(b.created_at)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEditBuyer(b)} className="p-1.5 rounded-md hover:bg-blue-50 text-[#A1A1AA] hover:text-blue-600 transition-colors" data-testid={`edit-buyer-${b.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDeleteBuyer(b.id)} className="p-1.5 rounded-md hover:bg-red-50 text-[#A1A1AA] hover:text-red-500 transition-colors" data-testid={`delete-buyer-${b.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Factories Tab */}
        {activeTab === 'factories' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA]">{factories.length} Factories</p>
              <Button
                onClick={() => { setEditingFactory(null); setFactoryForm({ name: '', location: '' }); setShowAddFactory(true); }}
                className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm btn-premium"
                data-testid="add-factory-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Factory
              </Button>
            </div>
            {factories.length === 0 ? (
              <div className="bg-white rounded-md border border-black/[0.06] p-10 text-center">
                <Factory className="w-10 h-10 text-[#D4D4D8] mx-auto mb-3" />
                <p className="text-sm text-[#A1A1AA]">No factories added yet</p>
                <p className="text-xs text-[#D4D4D8] mt-1">Add your first factory to start mapping orders</p>
              </div>
            ) : (
              <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden" data-testid="factories-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAF8] border-b border-black/[0.06]">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Factory Name</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Location</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Added</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {factories.map(f => (
                      <TableRow key={f.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`factory-row-${f.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0">
                              <Factory className="w-3.5 h-3.5 text-cyan-700" />
                            </div>
                            <span className="text-sm font-medium text-[#0A0A0A]">{f.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 text-[#A1A1AA]" />
                            <span className="text-sm text-[#52525B]">{f.location}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-[#71717A]">{formatDate(f.created_at)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEditFactory(f)} className="p-1.5 rounded-md hover:bg-blue-50 text-[#A1A1AA] hover:text-blue-600 transition-colors" data-testid={`edit-factory-${f.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDeleteFactory(f.id)} className="p-1.5 rounded-md hover:bg-red-50 text-[#A1A1AA] hover:text-red-500 transition-colors" data-testid={`delete-factory-${f.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div className="space-y-6">
            {/* Pending Invites */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA] mb-3">Pending ({pendingInvites.length})</h3>
              {pendingInvites.length === 0 ? (
                <div className="bg-white rounded-md border border-black/[0.06] p-6 text-center">
                  <UserPlus className="w-8 h-8 text-[#D4D4D8] mx-auto mb-2" />
                  <p className="text-sm text-[#A1A1AA]">No pending invites</p>
                  <p className="text-xs text-[#D4D4D8] mt-1">Click "Invite User" to create one</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="pending-invites-section">
                  {pendingInvites.map(inv => {
                    const rc = roleConfig[inv.role] || roleConfig.internal;
                    return (
                      <div key={inv.id} className="bg-white rounded-md border border-black/[0.06] p-4 flex items-center justify-between" data-testid={`invite-row-${inv.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-4 h-4 text-amber-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-[#0A0A0A]">{inv.email}</p>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${rc.bg} ${rc.text} capitalize`}>{rc.label}</span>
                            </div>
                            <p className="text-[11px] text-[#A1A1AA] mt-0.5">
                              {inv.name || 'No name'} {inv.company ? ` · ${inv.company}` : ''} · Invited {formatDate(inv.created_at)}
                              {inv.invited_by_name ? ` by ${inv.invited_by_name}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" className="rounded-md text-xs h-8 px-3" onClick={() => copyInviteLink(inv.token)} data-testid={`copy-invite-${inv.id}`}>
                            {copiedId === inv.token ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5" />Copy Link</>}
                          </Button>
                          <button onClick={() => handleDeleteInvite(inv.id)} className="p-2 rounded-md hover:bg-red-50 text-[#A1A1AA] hover:text-red-500 transition-colors" data-testid={`delete-invite-${inv.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Accepted Invites */}
            {acceptedInvites.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA] mb-3">Accepted ({acceptedInvites.length})</h3>
                <div className="space-y-1.5" data-testid="accepted-invites-section">
                  {acceptedInvites.map(inv => {
                    const rc = roleConfig[inv.role] || roleConfig.internal;
                    return (
                      <div key={inv.id} className="bg-[#FAFAF8] rounded-md border border-black/[0.04] px-4 py-3 flex items-center justify-between" data-testid={`accepted-invite-${inv.id}`}>
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-[#52525B]">{inv.email}</p>
                              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${rc.bg} ${rc.text}`}>{rc.label}</span>
                            </div>
                            <p className="text-[10px] text-[#A1A1AA]">Invited {formatDate(inv.created_at)}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-emerald-600 font-semibold">Joined</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Factory Dialog */}
      <Dialog open={showAddFactory} onOpenChange={(open) => { setShowAddFactory(open); if (!open) setEditingFactory(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editingFactory ? 'Edit Factory' : 'Add Factory'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Factory Name *</Label>
              <Input value={factoryForm.name} onChange={(e) => setFactoryForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Unit 1 - Noida" className="h-10 rounded-md" data-testid="factory-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Location *</Label>
              <Input value={factoryForm.location} onChange={(e) => setFactoryForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g., Noida, UP" className="h-10 rounded-md" data-testid="factory-location-input" />
            </div>
            <Button onClick={handleSaveFactory} disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="save-factory-btn">
              {saving ? 'Saving...' : editingFactory ? 'Update Factory' : 'Add Factory'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buyer Dialog */}
      <Dialog open={showAddBuyer} onOpenChange={(open) => { setShowAddBuyer(open); if (!open) setEditingBuyer(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editingBuyer ? 'Edit Buyer' : 'Add Buyer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Buyer Name *</Label>
              <Input value={buyerForm.name} onChange={(e) => setBuyerForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., John Smith" className="h-10 rounded-md" data-testid="buyer-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Buyer Email *</Label>
              <Input
                type="email"
                value={buyerForm.email}
                onChange={(e) => setBuyerForm(p => ({ ...p, email: e.target.value }))}
                placeholder="e.g., buyer@company.com"
                className="h-10 rounded-md"
                disabled={!!editingBuyer}
                data-testid="buyer-email-input"
              />
              {editingBuyer && <p className="text-[10px] text-[#A1A1AA]">Email cannot be changed after creation</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Buyer Contact</Label>
              <Input value={buyerForm.contact} onChange={(e) => setBuyerForm(p => ({ ...p, contact: e.target.value }))} placeholder="e.g., +91 98765 43210" className="h-10 rounded-md" data-testid="buyer-contact-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Buyer Company *</Label>
              <Input value={buyerForm.company} onChange={(e) => setBuyerForm(p => ({ ...p, company: e.target.value }))} placeholder="e.g., Zudio" className="h-10 rounded-md" data-testid="buyer-company-input" />
            </div>
            <Button onClick={handleSaveBuyer} disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="save-buyer-btn">
              {saving ? 'Saving...' : editingBuyer ? 'Update Buyer' : 'Add Buyer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={(open) => { setShowEditUser(open); if (!open) setEditingUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 mt-2">
              <div className="text-xs text-[#A1A1AA] bg-[#FAFAF8] rounded-md px-3 py-2 border border-black/[0.04]">
                <Mail className="w-3 h-3 inline mr-1.5" />{editingUser.email}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Name *</Label>
                <Input value={editUserForm.name} onChange={(e) => setEditUserForm(p => ({ ...p, name: e.target.value }))} className="h-10 rounded-md" data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Role *</Label>
                <Select value={editUserForm.role} onValueChange={(v) => setEditUserForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="h-10 rounded-md" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="merchant">Merchant</SelectItem>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Company</Label>
                <Input value={editUserForm.company} onChange={(e) => setEditUserForm(p => ({ ...p, company: e.target.value }))} className="h-10 rounded-md" data-testid="edit-user-company" />
              </div>
              {editUserForm.role === 'internal' && factories.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Assign Factory</Label>
                  <Select value={editUserForm.factory_id || 'none'} onValueChange={(v) => setEditUserForm(p => ({ ...p, factory_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="h-10 rounded-md" data-testid="edit-user-factory"><SelectValue placeholder="Select factory" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Factory</SelectItem>
                      {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name} — {f.location}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#A1A1AA]">Internal user will see all orders from this factory</p>
                </div>
              )}
              {isSuperAdmin && (
                <div className="space-y-3 pt-2 border-t border-black/[0.06]">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Permissions</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'can_view_orders', label: 'View Orders' },
                      { key: 'can_edit_orders', label: 'Edit Orders' },
                      { key: 'can_view_floor', label: 'View Floor Data' },
                      { key: 'can_edit_floor', label: 'Edit Floor Data' },
                    ].map(p => (
                      <label key={p.key} className="flex items-center gap-2.5 cursor-pointer group" data-testid={`perm-${p.key}`}>
                        <input
                          type="checkbox"
                          checked={userPerms[p.key] ?? true}
                          onChange={(e) => setUserPerms(prev => ({ ...prev, [p.key]: e.target.checked }))}
                          className="w-4 h-4 rounded border-black/[0.2] text-[#1C1C1C] focus:ring-[#1C1C1C]"
                        />
                        <span className="text-sm text-[#52525B] group-hover:text-[#0A0A0A] transition-colors">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={handleUpdateUser} disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="update-user-btn">
                {saving ? 'Updating...' : 'Update User'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
